import { useInfiniteQuery } from '@tanstack/react-query';
import { getPollVoters } from '@api-client/api-client';
import { pollVotersKey } from '@lib/cache/polls';
import type { SpaceUserSummary } from '@models/api/space';

/** One page of the "See who" list (TECH-197 § The API "Voters": 25 a page). */
export const POLL_VOTERS_PER_PAGE = 25;

export interface UsePollVotersOptions {
  questionId: string;
  /** One option's voters; null for every voter once. */
  optionId: string | null;
  /** While the drawer is open and the viewer may see names (`can_see_voters`). */
  enabled: boolean;
}

/**
 * Who answered a poll, newest first (CONTRACT § 4.5): page by page, one list per option. The list
 * is only ever asked for while `can_see_voters` holds, so the route's 403 means the card is out of
 * date; it shows as a load failure with a retry.
 */
export const usePollVoters = ({ questionId, optionId, enabled }: UsePollVotersOptions) => {
  const query = useInfiniteQuery({
    queryKey: pollVotersKey(questionId, optionId),
    queryFn: async ({ pageParam, signal }) =>
      (
        await getPollVoters(
          questionId,
          {
            ...(optionId ? { option_id: optionId } : {}),
            page: pageParam,
            per_page: POLL_VOTERS_PER_PAGE,
          },
          { signal },
        )
      ).data,
    initialPageParam: 1,
    getNextPageParam: lastPage =>
      lastPage.pagination.has_next ? lastPage.pagination.page + 1 : undefined,
    enabled: enabled && Boolean(questionId),
  });

  // A vote between two pages moves people up the list: the same person can come back on the next
  // page, so each shows once.
  const seen = new Set<string>();
  const voters: SpaceUserSummary[] = (query.data?.pages.flatMap(page => page.data) ?? []).filter(
    voter => (seen.has(voter.id) ? false : (seen.add(voter.id), true)),
  );

  return {
    voters,
    isLoading: query.isLoading,
    isError: query.isError,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchNextPageError: query.isFetchNextPageError,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
